import { appApi, idTag } from '@/app/redux/appApi';
import { OnboardingInfo, User } from '@/common/types/user';
import api from '@/db/apiClient';

export const userApi = appApi.injectEndpoints({
  endpoints: (build) => ({
    getUser: build.query<User | null, string>({
      query: async (userId) =>
        (await api.asEducator.getUserProfile(userId)) ?? null,
      onCacheEntryAdded: async (
        userId,
        { updateCachedData, cacheDataLoaded, cacheEntryRemoved },
      ) => {
        await cacheDataLoaded;
        const unsubscribe = await api.asEducator.subscribeToUser(
          userId,
          (user) => {
            updateCachedData(() => user);
          },
        );
        await cacheEntryRemoved;
        unsubscribe();
      },
      providesTags: (user) => (user ? [idTag('User', user.uid)] : []),
    }),

    getPendingInvite: build.query<string | null, string>({
      query: (email) =>
        api.asEducator.getFirstInviteTokenForEmail(email),
      providesTags: (res) => (res ? ['PendingInvite'] : []),
    }),

    completeOnboarding: build.mutation<
      { userId: string },
      { userId: string; marketingOptIn: boolean }
    >({
      query: async ({ userId, marketingOptIn }) => {
        await api.asEducator.updateUserMarketingOptin(userId, marketingOptIn);
        await api.asEducator.markUserOnboardingCompleted(userId);
        return { userId };
      },
      invalidatesTags: (res) => (res ? [idTag('User', res.userId)] : []),
    }),

    updateUserName: build.mutation<
      { userId: string },
      { userId: string; name: string }
    >({
      query: async ({ userId, name }) => {
        await api.asEducator.updateUserName(userId, name);
        return { userId };
      },
      invalidatesTags: (res) => (res ? [idTag('User', res.userId)] : []),
    }),

    updateOnboardingInfo: build.mutation<
      { userId: string },
      { userId: string; completed: boolean; onboardingInfo: OnboardingInfo }
    >({
      query: async ({ userId, completed, onboardingInfo }) => {
        await api.asEducator.updateOnboardingInfo(
          userId,
          completed,
          onboardingInfo,
        );
        return { userId };
      },
      invalidatesTags: (res) => (res ? [idTag('User', res.userId)] : []),
    }),

    updateMarketingOptIn: build.mutation<
      { userId: string },
      { userId: string; marketingOptIn: boolean }
    >({
      query: async ({ userId, marketingOptIn }) => {
        await api.asEducator.updateUserMarketingOptin(userId, marketingOptIn);
        return { userId };
      },
      invalidatesTags: (res) => (res ? [idTag('User', res.userId)] : []),
    }),

    markTourViewed: build.mutation<
      { userId: string },
      { userId: string; tourKey: string }
    >({
      query: async ({ userId, tourKey }) => {
        await api.asEducator.markTourViewed(userId, tourKey);
        return { userId };
      },
      invalidatesTags: (res) => (res ? [idTag('User', res.userId)] : []),
    }),
  }),
});

export const {
  useGetUserQuery,
  useGetPendingInviteQuery,
  useCompleteOnboardingMutation,
  useUpdateUserNameMutation,
  useUpdateOnboardingInfoMutation,
  useUpdateMarketingOptInMutation,
  useMarkTourViewedMutation,
} = userApi;
